import React, {useEffect, useState} from "react";
import {useTheme} from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {useNavigate, useLocation} from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {Alert, AppBar, Card, Checkbox, FormControlLabel, Snackbar, Typography} from "@mui/material";
import MDButton from "components/MDButton";
import {PATCH, GET} from "APIclient";
import properties from "constants/ApplicationConstants";
import {CircularProgress} from "@mui/material";

const capitalizeFirstLetter = string => {
  return string
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function EditPermissions(props) {
  const location = useLocation();
  const empId = location.state || window.sessionStorage.getItem("empId");
  const theme = useTheme();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [checked, setChecked] = useState({
    details: false,
    dependents: false,
    address: false,
    bank_details: false,
    documents: false,
    experience: false,
    add_technology: false,
    upload_image: false
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoading(true);
        const response = await GET(`admin/employee/permissions?empId=${empId}`);
        if (response?.data?.statusCode === 200) {
          const permissions = response.data.data;
          const updatedChecked = {...checked};
          Object.keys(updatedChecked).forEach(key => {
            updatedChecked[key] = permissions.includes(key);
          });
          setChecked(updatedChecked);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (empId) {
      fetchPermissions();
    }
  }, [empId]);

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = event => {
    const {name, checked: isChecked} = event.target;
    setChecked(prevState => ({
      ...prevState,
      [name]: isChecked
    }));
  };

  const handleSubmit = async () => {
    const accessArray = Object.keys(checked).filter(key => checked[key]);

    const dt = {
      empId: empId,
      access: accessArray
    };

    const url = `admin/updateAccess?empId=${empId}`;

    try {
      const response = await PATCH(url, dt);
      if (response?.data?.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage(response?.data.message || "Permissions updated successfully");
        setOpen(true);
      } else {
        setSnakColor("error");
        setSnakMessage("Failed to update permissions");
        setOpen(true);
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      setSnakColor("error");
      setSnakMessage("Error updating permissions");
      setOpen(true);
    }
  };

  const isUpdateEnabled = Object.values(checked).some(value => value === true);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        container
        sx={{
          height: "80vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <AppBar position="static">
          <Card>
            <MDBox
              pt={1}
              mb={0}
              pb={1}
              px={4}
              sx={{borderRadius: "5px", border: "1px solid #D3D3D3"}}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                Editing Permissions of Employee: {empId}
              </Typography>
            </MDBox>

            <MDBox pt={1} mb={0} px={4}>
              {isLoading && empId ? (
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "40vh"
                  }}
                >
                  <CircularProgress color="info" />
                </MDBox>
              ) : (
                <>
                  <FormControlLabel
                    label="Check All"
                    control={
                      <Checkbox
                        checked={Object.values(checked).every(Boolean)}
                        indeterminate={
                          Object.values(checked).some(Boolean) &&
                          !Object.values(checked).every(Boolean)
                        }
                        onChange={e => {
                          const newChecked = Object.fromEntries(
                            Object.keys(checked).map(key => [key, e.target.checked])
                          );
                          setChecked(newChecked);
                        }}
                      />
                    }
                  />

                  <MDBox sx={{display: "flex", flexDirection: "column", ml: 3}}>
                    {Object.keys(checked).map(permission => (
                      <FormControlLabel
                        key={permission}
                        label={capitalizeFirstLetter(permission)}
                        name={permission}
                        control={<Checkbox checked={checked[permission]} onChange={handleChange} />}
                      />
                    ))}
                  </MDBox>
                </>
              )}
            </MDBox>

            <MDBox mb={2} sx={{display: "flex", justifyContent: "flex-end"}}>
              <MDBox sx={{display: "flex", gap: 4}}>
                <MDButton
                  variant="gradient"
                  color="warning"
                  sx={{width: "25ch", fontFamily: properties.fontFamily}}
                  onClick={() => navigate(-1)}
                >
                  Back
                </MDButton>
              </MDBox>
              <MDButton
                variant="gradient"
                color="info"
                sx={{
                  width: "25ch",
                  ml: 2,
                  mr: 2,
                  fontFamily: properties.fontFamily,
                  background: properties.colors.bgcolorbluethem
                }}
                onClick={handleSubmit}
                disabled={!isUpdateEnabled || isLoading}
              >
                Update
              </MDButton>
            </MDBox>

            <Snackbar
              open={open}
              anchorOrigin={{vertical: "top", horizontal: "right"}}
              autoHideDuration={1500}
              onClose={handleClose}
            >
              <Alert
                onClose={handleClose}
                severity={snakColor === "success" ? "success" : "error"}
                variant="filled"
                sx={{width: "100%", color: "#fff"}}
              >
                {snakMessage}
              </Alert>
            </Snackbar>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );
}

export default EditPermissions;
